import type { HackleEvent } from '@hackler/react-sdk';
import { createInstance } from '@hackler/react-sdk';
import { isClientSide, isServerSide } from '@zelos/shared/lib/environment';
import { useDetailUser, useIsSignedIn } from '@zelos/shared/lib/sign.service';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

type CustomTrackOptions = {
  force?: boolean,
};

const isProduction = process.env.IS_PRODUCTION;
const hackleAppKey = process.env.HACKLE_BROWSER_KEY || '';

export const hackleClient = createInstance(hackleAppKey, {
  debug: !isProduction,
});

const hackleUserSetAtom = atom<boolean>(false);

export const useCustomHackleTrack = () => {
  const isHackleUserSet = useAtomValue(hackleUserSetAtom);

  const calledTrack = useRef<Set<string>>(new Set());
  const tracksBeforeSignedCheck = useRef<Array<{ event: HackleEvent, options?: CustomTrackOptions }>>([]);

  const customTrack = useCallback((event: HackleEvent, options?: CustomTrackOptions) => {
    if (!isClientSide) return;
    if (!calledTrack.current.has(event.key) || options?.force) {
      hackleClient.track(event);
      calledTrack.current.add(event.key);
    }
  }, []);

  useEffect(() => {
    if (!isHackleUserSet || !tracksBeforeSignedCheck.current.length) return;
    tracksBeforeSignedCheck.current.forEach(({ event, options }) => {
      customTrack(event, options);
    });
    tracksBeforeSignedCheck.current = [];
  }, [customTrack, isHackleUserSet]);

  return {
    track: useCallback((event: HackleEvent, options?: CustomTrackOptions) => {
      if (!isHackleUserSet) {
        tracksBeforeSignedCheck.current.push({ event, options });
      } else {
        customTrack(event, options);
      }
    }, [customTrack, isHackleUserSet]),
  };
};

type UseImmediateHackleTrackProps = {
  event: HackleEvent,
};

export const useImmediateHackleTrack = ({ event }: UseImmediateHackleTrackProps) => {
  const { track } = useCustomHackleTrack();

  useEffect(() => {
    track(event);
  }, [event, track]);
};

export const useSetHackleUser = () => {
  const isSignedIn = useIsSignedIn();
  const detailUser = useDetailUser();
  const setIsHackleUserSet = useSetAtom(hackleUserSetAtom);

  useEffect(() => {
    if (isSignedIn === null || isServerSide) return;
    if (!isSignedIn) {
      hackleClient.resetUser();
      setIsHackleUserSet(true);
    } else if (isSignedIn && detailUser) {
      hackleClient.setUserId(String(detailUser.programmersUserId));
      setIsHackleUserSet(true);
    }
    return () => {
      setIsHackleUserSet(false);
    };
  }, [detailUser, isSignedIn, setIsHackleUserSet]);
};
